/**
 * Login Page
 * Handles user authentication
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import AuthLayout from '../../components/auth/AuthLayout';
import FormInput from '../../components/auth/FormInput';
import Button from '../../components/auth/Button';
import Alert from '../../components/auth/Alert';
import OAuthButton from '../../components/auth/OAuthButton';
import { validateLoginForm, hasErrors } from '../../utils/validation';
import { FormErrors, ApiError } from '../../types/auth';
import { API_BASE_URL as API_URL } from '../../config';
import { sendVerificationEmailFor } from '../../services/authService';

const LOGIN_ERROR_CODE_TO_I18N: Record<string, string> = {
  INVALID_CREDENTIALS: 'errors.invalidCredentials',
  ACCOUNT_NOT_ACTIVE: 'errors.accountNotActive',
  EMAIL_NOT_VERIFIED: 'errors.emailNotVerified',
  PARENTAL_CONSENT_REQUIRED: 'errors.parentalConsentRequired',
  NETWORK_ERROR: 'errors.networkError',
};

const LoginPage: React.FC = () => {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login, isAuthenticated, isLoading, clearError } = useAuth();

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [submitLoading, setSubmitLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [loginErrorCode, setLoginErrorCode] = useState<string | null>(null);
  const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [resendErrorMessage, setResendErrorMessage] = useState<string | null>(null);

  // Success message from registration or password reset
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Check for success message from redirect
  useEffect(() => {
    const state = location.state as { message?: string } | null;
    if (state?.message) {
      setSuccessMessage(state.message);
      // Clear the state to prevent message showing again on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      // Check URL param first, then location state, then default to home
      const redirectParam = searchParams.get('redirect');
      const from = redirectParam || (location.state as { from?: { pathname: string } } | null)?.from?.pathname || '/';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location, searchParams]);

  // Clear auth error when component unmounts or inputs change
  useEffect(() => {
    return () => clearError();
  }, [clearError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormErrors({});
    clearError();
    setLoginError(null);
    setLoginErrorCode(null);
    setResendState('idle');
    setResendErrorMessage(null);

    // Validate form
    const errors = validateLoginForm({ email, password });
    if (hasErrors(errors)) {
      setFormErrors(errors);
      return;
    }

    setSubmitLoading(true);

    try {
      await login({ email, password, rememberMe });
      // Navigate is handled by useEffect above when isAuthenticated changes
    } catch (err) {
      const apiError = err as ApiError;
      const i18nKey = apiError?.code ? LOGIN_ERROR_CODE_TO_I18N[apiError.code] : undefined;
      setLoginError(i18nKey ? t(i18nKey) : t('errors.loginFailed'));
      setLoginErrorCode(apiError?.code ?? null);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email || resendState === 'sending' || resendState === 'sent') return;
    setResendState('sending');
    setResendErrorMessage(null);
    try {
      await sendVerificationEmailFor(email);
      setResendState('sent');
    } catch (err) {
      const apiError = err as ApiError;
      setResendState('error');
      if (apiError?.code === 'EMAIL_ALREADY_VERIFIED') {
        setResendErrorMessage(t('errors.emailAlreadyVerified', { defaultValue: 'This email is already verified. Please try logging in again.' }));
      } else if (apiError?.statusCode === 429) {
        setResendErrorMessage(t('errors.resendRateLimited', { defaultValue: 'Too many requests. Please try again later.' }));
      } else {
        setResendErrorMessage(t('errors.resendFailed', { defaultValue: 'Failed to send verification email. Please try again.' }));
      }
    }
  };

  return (
    <AuthLayout
      title={t('login.title')}
      subtitle={t('login.subtitle')}
    >
      {successMessage && (
        <Alert type="success" onDismiss={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {loginError && (
        <Alert type="error" onDismiss={() => { setLoginError(null); setLoginErrorCode(null); }}>
          <div>{loginError}</div>
          {loginErrorCode === 'EMAIL_NOT_VERIFIED' && (
            <div className="mt-2">
              {resendState === 'sent' ? (
                <span className="text-green-700 font-medium">
                  {t('login.verificationSent', { defaultValue: 'Verification email sent. Please check your inbox.' })}
                </span>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={handleResendVerification}
                    disabled={resendState === 'sending' || !email}
                    className="text-primary-600 hover:text-primary-700 font-medium underline disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {resendState === 'sending'
                      ? t('login.sendingVerification', { defaultValue: 'Sending…' })
                      : t('login.resendVerification', { defaultValue: 'Resend verification email' })}
                  </button>
                  {resendState === 'error' && resendErrorMessage && (
                    <div className="mt-1 text-xs text-red-700">{resendErrorMessage}</div>
                  )}
                </>
              )}
            </div>
          )}
        </Alert>
      )}

      <form onSubmit={handleSubmit} noValidate>
        <FormInput
          label={t('login.email')}
          type="email"
          name="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={formErrors.email}
          placeholder={t('placeholders.email')}
          autoComplete="email"
          disabled={submitLoading}
        />

        <div className="relative">
          <FormInput
            label={t('login.password')}
            type={showPassword ? 'text' : 'password'}
            name="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={formErrors.password}
            placeholder={t('placeholders.password')}
            autoComplete="current-password"
            disabled={submitLoading}
          />
          <button
            type="button"
            className="absolute right-3 top-[38px] text-gray-400 hover:text-gray-600"
            onClick={() => setShowPassword(!showPassword)}
            tabIndex={-1}
          >
            {showPassword ? (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L6.59 6.59m7.532 7.532l3.29 3.29M3 3l18 18" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            )}
          </button>
        </div>

        <div className="flex items-center justify-between mb-6">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              disabled={submitLoading}
            />
            <span className="ml-2 text-sm text-gray-600">{t('login.rememberMe')}</span>
          </label>

          <Link
            to="/forgot-password"
            className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
          >
            {t('login.forgotPassword')}
          </Link>
        </div>

        <Button
          type="submit"
          fullWidth
          isLoading={submitLoading || isLoading}
          disabled={submitLoading || isLoading}
        >
          {t('login.submit')}
        </Button>
      </form>

      {/* OAuth Divider */}
      <div className="my-6 flex items-center">
        <div className="flex-1 border-t border-gray-300" />
        <span className="px-4 text-sm text-gray-500">{t('login.orContinueWith')}</span>
        <div className="flex-1 border-t border-gray-300" />
      </div>

      {/* OAuth Buttons */}
      <div className="space-y-3">
        <OAuthButton
          provider="google"
          label={t('oauth.continueWithGoogle')}
          onClick={() => {
            window.location.href = `${API_URL}/api/v1/auth/google`;
          }}
          disabled={submitLoading || isLoading}
        />
        <OAuthButton
          provider="github"
          label={t('oauth.continueWithGithub')}
          onClick={() => {
            window.location.href = `${API_URL}/api/v1/auth/github`;
          }}
          disabled={submitLoading || isLoading}
        />
      </div>

      <div className="mt-6 text-center">
        <p className="text-sm text-gray-600">
          {t('login.noAccount')}{' '}
          <Link
            to="/register"
            className="font-medium text-primary-600 hover:text-primary-700 hover:underline"
          >
            {t('login.signUp')}
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
};

export default LoginPage;
